/**
 * useJobDetailsSyncStatus - Tracks how many galleries have job details synced.
 *
 * Compares galleries with job_number against jobber_job_details table
 * to show synced vs not-synced counts.
 *
 * @see /src/features/gallery/hooks/useJobberJobDetails.ts - Individual job details fetch
 * @see /apps/production/jobber-image-processor - Worker that populates jobber_job_details
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface JobDetailsSyncStatus {
  /** Galleries with job details in jobber_job_details table */
  synced: number
  /** Galleries with job_number but no job details */
  notSynced: number
  /** Galleries without any job_number */
  noJobNumber: number
  /** Total galleries */
  total: number
}

/**
 * Fetches sync status by comparing galleries to jobber_job_details.
 * Uses pagination to avoid Supabase default 1000 row limit.
 */
async function fetchJobDetailsSyncStatus(): Promise<JobDetailsSyncStatus> {
  // Get synced job_numbers from jobber_job_details with pagination
  // IMPORTANT: Default Supabase limit is 1000, so we must paginate
  const allJobDetails: { job_number: number; franchise_region: string }[] = []
  let offset = 0
  const batchSize = 1000

  while (true) {
    const { data: jobDetails, error: jobDetailsError } = await supabase
      .from('jobber_job_details')
      .select('job_number, franchise_region')
      .range(offset, offset + batchSize - 1)

    if (jobDetailsError) {
      console.error('Error fetching job details for sync status:', jobDetailsError)
      throw jobDetailsError
    }

    if (!jobDetails || jobDetails.length === 0) break

    allJobDetails.push(...jobDetails)

    if (jobDetails.length < batchSize) break
    offset += batchSize
  }

  // Build a set of synced job_number + franchise combinations
  // Handle franchise mapping differences between tables
  const syncedSet = new Set<string>()
  for (const jd of allJobDetails) {
    // Map DB franchise format to gallery format for comparison
    const franchiseVariants = [jd.franchise_region]
    if (jd.franchise_region === 'northyork') franchiseVariants.push('north_york')
    if (jd.franchise_region === 'hamilton') franchiseVariants.push('HQ')

    for (const fr of franchiseVariants) {
      syncedSet.add(`${jd.job_number}|${fr}`)
    }
  }

  // Get total gallery count
  const { count: totalCount, error: countError } = await supabase
    .from('galleries')
    .select('*', { count: 'exact', head: true })

  if (countError) {
    console.error('Error fetching total gallery count:', countError)
    throw countError
  }

  // Get count of galleries with job_number
  const { count: withJobNumberCount, error: withJobNumberError } = await supabase
    .from('galleries')
    .select('*', { count: 'exact', head: true })
    .not('job_number', 'is', null)

  if (withJobNumberError) {
    console.error('Error fetching galleries with job_number count:', withJobNumberError)
    throw withJobNumberError
  }

  // Now fetch galleries in batches to count synced vs not synced
  // Use pagination to avoid the 1000 row limit
  let synced = 0
  let notSynced = 0
  let galleryOffset = 0
  const galleryBatchSize = 1000

  while (true) {
    const { data: galleries, error: galleriesError } = await supabase
      .from('galleries')
      .select('job_number, franchise_region')
      .not('job_number', 'is', null)
      .range(galleryOffset, galleryOffset + galleryBatchSize - 1)

    if (galleriesError) {
      console.error('Error fetching galleries for sync status:', galleriesError)
      throw galleriesError
    }

    if (!galleries || galleries.length === 0) break

    for (const g of galleries) {
      const key = `${g.job_number}|${g.franchise_region}`
      if (syncedSet.has(key)) {
        synced++
      } else {
        notSynced++
      }
    }

    if (galleries.length < galleryBatchSize) break
    galleryOffset += galleryBatchSize
  }

  const noJobNumber = (totalCount || 0) - (withJobNumberCount || 0)

  return {
    synced,
    notSynced,
    noJobNumber,
    total: totalCount || 0,
  }
}

/**
 * Hook to fetch job details sync status across all galleries
 */
export function useJobDetailsSyncStatus() {
  return useQuery<JobDetailsSyncStatus>({
    queryKey: ['job-details-sync-status'],
    queryFn: fetchJobDetailsSyncStatus,
    staleTime: 2 * 60 * 1000, // 2 minutes - refresh fairly often
    refetchOnWindowFocus: true,
  })
}

export default useJobDetailsSyncStatus
